import { stringUtils } from '@/Utils';
import { Nullable } from '@/Types';
import { OrderTableRow } from '@/Types';
import { CancelStatus, InvoiceType, MarketplaceType, NumberGenerationType, OrderStatus, PaymentStatus } from '@/Enums';
import { OrderItemProductShortTableRow } from '@/Types/OrderItemProductShortTableRow';
import { OrderItemModel } from '@/Models/Order/OrderItemModel';

function getInvoiceNumbers(order: Nullable<OrderTableRow>): string[] {
  return order?.invoices?.map((inv) => inv.number).filter(Boolean) ?? [];
}

function getCancelInvoiceNumbers(order: Nullable<OrderTableRow>): string[] {
  return (
    order?.invoices
      ?.map((inv) => inv.orderCancelInvoice)
      .filter(Boolean)
      .map((cancelInvoice) => cancelInvoice!.number) ?? []
  );
}

const getDocumentNumbersByType = (
  documentType: NumberGenerationType | undefined | null,
  order: Nullable<OrderTableRow>,
): string[] => {
  if (!documentType || !order) return [];

  switch (documentType) {
    case NumberGenerationType.SaleOrder:
      return order.orderNumber ? [order.orderNumber] : [];

    case NumberGenerationType.PackList:
      return order.deliveryNoteNumber ? [order.deliveryNoteNumber] : [];

    case NumberGenerationType.Invoice:
      return getInvoiceNumbers(order);

    case NumberGenerationType.CancelInvoice:
      return getCancelInvoiceNumbers(order);

    default:
      return [];
  }
};

const isInvoiceAndOriginalShippingAddressesEqual = (orderTableRow: Nullable<OrderTableRow>): boolean => {
  return (
    stringUtils.isStringsEqual(orderTableRow?.invoiceCompanyName, orderTableRow?.originalShippingCompanyName) &&
    stringUtils.isStringsEqual(orderTableRow?.invoiceFirstName, orderTableRow?.originalShippingFirstName) &&
    stringUtils.isStringsEqual(orderTableRow?.invoiceLastName, orderTableRow?.originalShippingLastName) &&
    stringUtils.isStringsEqual(orderTableRow?.invoiceAdditionalLine, orderTableRow?.originalShippingAdditionalLine) &&
    stringUtils.isStringsEqual(orderTableRow?.invoiceStreet, orderTableRow?.originalShippingStreet) &&
    stringUtils.isStringsEqual(orderTableRow?.invoiceHouseNo, orderTableRow?.originalShippingHouseNo) &&
    stringUtils.isStringsEqual(orderTableRow?.invoicePostalCode, orderTableRow?.originalShippingPostalCode) &&
    stringUtils.isStringsEqual(orderTableRow?.invoiceCityName, orderTableRow?.originalShippingCityName) &&
    stringUtils.isStringsEqual(orderTableRow?.invoiceCountryCode, orderTableRow?.originalShippingCountryCode)
  );
};

function isCreatingCancelInvoiceDisabled(order: Nullable<OrderTableRow>): boolean {
  const noInvoicesCreated = getInvoiceNumbers(order).length == 0;
  const currentCancelInvoiceExists = Boolean(order?.currentCancelInvoiceCreatedTime);

  return currentCancelInvoiceExists || noInvoicesCreated;
}

const isOriginalShippingModified = (orderTableRow: Nullable<OrderTableRow>): boolean => {
  return !!orderTableRow?.isOriginalShippingModified;
};

const isOrderIsEbayPlus = (orderRow: Nullable<OrderTableRow>) => {
  if (!orderRow) return false;
  return orderRow.orderItems.some((item) => Boolean(item.ebayPlus));
};

const isOrderIsAmazonPending = (order: OrderTableRow) => {
  return order.orderStatus === OrderStatus.Pending && order.marketplaceType === MarketplaceType.Amazon;
};

function isOrderPayed(orderRow: OrderTableRow): boolean {
  return [PaymentStatus.Paid, PaymentStatus.OverPaid].includes(orderRow.paymentStatus) && orderRow.refundAmount === 0;
}

function isOrderShipped(orderRow: OrderTableRow): boolean {
  return Boolean(orderRow.shippedTime);
}

function isOrderReceived(orderRow: OrderTableRow): boolean {
  return Boolean(orderRow.actualDeliveryTime);
}

function isInternationalOrder(orderRow: OrderTableRow): boolean {
  if (orderRow.shippingCountryCode) {
    return orderRow.shippingCountryCode != orderRow.shipFromCountry;
  }
  if (orderRow.originalShippingCountryCode) {
    return orderRow.originalShippingCountryCode != orderRow.shipFromCountry;
  }

  return false;
}

function isOrderCanceled(orderRow: Nullable<OrderTableRow>): boolean {
  if (!orderRow) return false;

  return [
    CancelStatus.CanceledNotPaid,
    CancelStatus.CanceledNoRefund,
    CancelStatus.CanceledRefunded,
    CancelStatus.CanceledWithoutReturning,
    CancelStatus.CanceledReturned,
    CancelStatus.CancelCompleted,
    CancelStatus.FulfillmentCancel,
  ].includes(orderRow.cancelStatus);
}

function calculateOrderPromotionFee(orderRow: Nullable<OrderTableRow>): number {
  if (!orderRow) return 0;
  return orderRow.orderItems.reduce((acc, item) => acc + item.promotionFee, 0);
}

function calculateProductPrice(productItem: OrderItemProductShortTableRow, invoiceType: InvoiceType): number {
  return +(
    productItem.quantity *
    (invoiceType === InvoiceType.GrossInvoice ? Number(productItem.priceBrutto) : Number(productItem.priceNetto))
  ).toFixed(2);
}

function calculateOrderItemPrice(invoiceType: InvoiceType, orderItem: OrderItemModel): number {
  const price = invoiceType === InvoiceType.GrossInvoice ? Number(orderItem.priceBrutto) : Number(orderItem.priceNetto);
  return orderItem.quantity * price;
}

function calculatePriceBrutto(priceNetto: number, vat: number): number {
  return +(priceNetto * (1 + vat / 100)).toFixed(2);
}

function calculatePriceNetto(priceBrutto: number, vat: number): number {
  return +(priceBrutto / (1 + vat / 100)).toFixed(2);
}

export const orderUtils = {
  getInvoiceNumbers,
  getCancelInvoiceNumbers,
  getDocumentNumbersByType,

  isCreatingCancelInvoiceDisabled,
  isInternationalOrder,
  isOriginalShippingModified,
  isInvoiceAndOriginalShippingAddressesEqual,
  isOrderIsAmazonPending,
  isOrderIsEbayPlus,
  isOrderPayed,
  isOrderShipped,
  isOrderReceived,
  isOrderCanceled,

  calculateOrderPromotionFee,
  calculateProductPrice,
  calculateOrderItemPrice,
  calculatePriceBrutto,
  calculatePriceNetto,
};
